import { useCallback } from 'react'
import { useCalendarClient } from '@frontend/clients/calendar.request'
import { useDebounce } from '@frontend/components/hooks'
import { dayjs } from '@frontend/lib/dayjs-plugins'
import CalendarBookingStore from '@frontend/stores/CalendarBookingStore'
import { useQuery, useQueryClient } from '@tanstack/react-query'

import { InboundOutboundType, SlotState } from '@shared/const'

import { ListBookingCapacitiesFilters, UISlotListFilterParams } from '@shared-types/calendar'
import { Identifier, Nullable, Optional } from '@shared-types/util'

import { fetchAllPages } from './utils'

export function useQueryAllFacilityBookings(
  facilityId: Nullable<Identifier>,
  filters: UISlotListFilterParams,
  refetchInterval?: number,
  enabled = true
) {
  const calendarClient = useCalendarClient()
  const debouncedFilters = useDebounce(filters)

  return useQuery({
    queryKey: ['facility_bookings', 'all', facilityId, debouncedFilters],

    queryFn: async () => {
      if (!facilityId) {
        return []
      }

      const results = await fetchAllPages(debouncedFilters, (fAFilters) =>
        calendarClient.all(facilityId, fAFilters)
      )
      return results.results
    },

    refetchInterval,
    enabled,
  })
}

export function useQueryFacilitySlotsPaginated(
  facilityId: Nullable<Identifier>,
  filters: UISlotListFilterParams,
  refetchInterval?: number
) {
  const calendarClient = useCalendarClient()
  const debouncedFilters = useDebounce(filters)

  return useQuery({
    queryKey: ['facility_bookings', facilityId, debouncedFilters],

    queryFn: async () => {
      if (!facilityId) {
        return { results: [] }
      }

      return calendarClient.all(facilityId, debouncedFilters)
    },

    refetchInterval,
  })
}

export function useQueryReservedTimeSlots(
  facilityId: Nullable<Identifier>,
  sendingCompanyId?: Identifier
) {
  const calendarStartTime = CalendarBookingStore.useState((s) => s.filters.start_time)

  const startTime = dayjs.utc(calendarStartTime)
  const endOfNextMonth = dayjs().utc().add(1, 'month').endOf('month')

  const filters: UISlotListFilterParams = {
    start_time: startTime.startOf('day').toISOString(),
    end_time: endOfNextMonth.endOf('day').toISOString(),
    state: SlotState.ACTIVE,
    reserved_for_company_id: sendingCompanyId,
    shipment_id: 'null',
  }

  const calendarClient = useCalendarClient()
  const debouncedFilters = useDebounce(filters)

  return useQuery({
    queryKey: ['facility_bookings', facilityId, debouncedFilters],

    queryFn: async () => {
      if (!facilityId || !sendingCompanyId) {
        return []
      }

      const results = await fetchAllPages(debouncedFilters, (fAFilters) =>
        calendarClient.all(facilityId, fAFilters)
      )
      return results.results
    },
  })
}

export function useQueryRegularTimeSlots(
  facilityId: Nullable<Identifier>,
  type: InboundOutboundType,
  departmentId: Optional<Identifier>
) {
  const calendarFilters = CalendarBookingStore.useState((s) => s.filters)
  const filters: UISlotListFilterParams = {
    ...calendarFilters,
    reserved_for_company_id: 'null',
    shipment_id: 'null',
    type,
    department_id: departmentId,
  }
  return useQueryAllFacilityBookings(facilityId, filters)
}

export function useQueryFacilityBookingDetails(
  facilityId: Nullable<Identifier>,
  bookingId: Nullable<Identifier>
) {
  const calendarClient = useCalendarClient()

  return useQuery({
    queryKey: ['facility_booking_details', facilityId, bookingId],

    queryFn: async () => {
      if (!facilityId || !bookingId) {
        return null
      }

      return calendarClient.getBookingDetails(facilityId, bookingId)
    },
  })
}

export function useClearBookingsCache() {
  const queryClient = useQueryClient()
  return useCallback(() => {
    return Promise.all([
      queryClient.invalidateQueries({ queryKey: ['facility_bookings'] }),
      queryClient.invalidateQueries({ queryKey: ['facility_booking_details'] }),
      queryClient.invalidateQueries({ queryKey: ['facility_booking_capacities'] }),
    ])
  }, [queryClient])
}

type QueryBookingCapacitiesParams = {
  facilityId: Nullable<Identifier>
  filters: ListBookingCapacitiesFilters
  refetchInterval?: number
}

export function useQueryBookingCapacities({
  facilityId,
  filters,
  refetchInterval,
}: QueryBookingCapacitiesParams) {
  const calendarClient = useCalendarClient()
  const debouncedFilters = useDebounce(filters)
  // filters and debouncedFilters diverge while the debounce timer is pending, e.g. right after
  // switching months, before the query itself has re-run for the new range
  const isDebouncingFilters = JSON.stringify(filters) !== JSON.stringify(debouncedFilters)

  const query = useQuery({
    queryKey: ['facility_booking_capacities', facilityId, debouncedFilters],

    queryFn: async () => {
      if (!facilityId) {
        return null
      }
      return calendarClient.getBookingCapacities(facilityId, debouncedFilters)
    },

    refetchInterval,
  })

  return { ...query, isPending: query.isPending || isDebouncingFilters }
}

type QuerySlotCapacitiesParams = {
  token: string
  filters: ListBookingCapacitiesFilters
}

export function useQuerySlotCapacities({ token, filters }: QuerySlotCapacitiesParams) {
  const calendarClient = useCalendarClient()

  return useQuery({
    queryKey: ['facility_booking_capacities', token, filters],

    queryFn: async () => {
      return calendarClient.getSlotCapacities(token, filters)
    },
  })
}

type FreeTimeSlotParams = {
  token: string
  start_date?: string
  end_date?: string
  reserved?: boolean
  type?: InboundOutboundType
  truck_type?: ListBookingCapacitiesFilters['truck_type']
}
export function useQueryGetFreeTimeSlots({
  token,
  start_date,
  end_date,
  reserved = false,
  type = InboundOutboundType.INBOUND,
  truck_type,
}: FreeTimeSlotParams) {
  const calendarClient = useCalendarClient()

  return useQuery({
    queryKey: [
      'new_bookings_free_time_slots',
      token,
      start_date,
      end_date,
      reserved,
      type,
      truck_type,
    ],

    queryFn: async () => {
      if (!start_date || !end_date) {
        return { results: [] }
      }
      return calendarClient.getFreeTimeSlots(
        token,
        start_date,
        end_date,
        reserved,
        type,
        truck_type
      )
    },
  })
}
